import { optimizeFile, loadImage, isOverSize, validateUrl } from './Helper'
import uniq from 'lodash/uniq'
import { BannerBoundingSizeModel } from './BannerBoundingSize'
import { BannerRepository, fileToBase64 } from '.'
import { OverSizeImageError, AddBannersError } from './Error'

/**
 * バナーを一括登録するフォーム
 */
export class AddBannersFormModel {
  constructor({ creativeFolderId }) {
    this.creativeFolderId = creativeFolderId
    this.enableBulkSetting = true
    this.landingPageUrl = ''
    this.banners = []
    this.isCheckOverCompressionRate = false
  }

  get isValid() {
    return (
      this.banners.length > 0 &&
      (!this.enableBulkSetting || validateUrl(this.landingPageUrl)) &&
      this.banners.every(b => b.isValid) &&
      this.bannerNamesAreUnique
    )
  }

  /**
   * クリエイティブ名が一意かどうか
   * 一部登録失敗時に成功したものを除去するためのルール。
   */
  get bannerNamesAreUnique() {
    return (
      uniq(this.banners.map(banner => banner.name)).length ===
      this.banners.length
    )
  }

  /**
   * 他のバナーと名前が被っていないかどうか
   * @param {AddBannerFormModel} banner
   * @return {boolean}
   */
  isBannerNameUnique(banner) {
    return this.banners.filter(b => b.name === banner.name).length === 1
  }

  /**
   * 遷移先URLを一括設定する。
   * @param {string} landingPageUrl
   */
  bulkSetLandingPageUrl(landingPageUrl) {
    if (!this.enableBulkSetting) return
    this.banners.forEach(b => (b.landingPageUrl = landingPageUrl))
  }

  /**
   * @param files
   * @returns void
   * 例外を投げた時は、AddBannerErrorsをthrowする
   */
  async addBanners(files) {
    const landingPageUrl = this.landingPageUrl
    let banners = []
    const addBannerErrors = new AddBannersError()

    for (let i = 0; i < files.length; i++) {
      const fileName = files[i].name
      const result = await optimizeFile(files[i])
      /** optimizeに失敗した場合はcontinue */
      if (result instanceof Error) {
        addBannerErrors.add(fileName, result)
        continue
      }
      const { optimizedFile, isCheckOverCompressionRate } = result
      const image = await loadImage(optimizedFile)
      const addBannerFormModel = new AddBannerFormModel({
        file: optimizedFile,
        name: fileName,
        landingPageUrl,
        image,
        isCheckOverCompressionRate,
      })
      /** 画像サイズが大きすぎる場合はcontinue */
      if (addBannerFormModel.isOverSize) {
        addBannerErrors.add(fileName, new OverSizeImageError())
        continue
      }
      banners = banners.concat(addBannerFormModel)
    }
    this.banners = this.banners.concat(banners)
    if (addBannerErrors.isEmpty()) return true
    throw addBannerErrors
  }

  deleteBanner(index) {
    this.banners.splice(index, 1)
  }

  checkedOverCompressionRate(index) {
    this.banners[index].isCheckOverCompressionRate = false
  }

  async save() {
    const creatives = await Promise.all(
      this.banners.map(async banner => {
        const base64ImageData = await fileToBase64(banner.file)
        return {
          creativeFolderId: this.creativeFolderId,
          name: banner.name,
          fileName: banner.filename,
          width: banner.normalizedBoundingSize.width,
          height: banner.normalizedBoundingSize.height,
          landingPageUrl: banner.normalizedLandingPageUrl,
          base64ImageData,
        }
      }),
    )
    try {
      return await BannerRepository.bulkCreate(creatives)
    } catch (e) {
      this.removeSavedBanners(e.results)
      throw new Error(`${e.message}もう一度お試しください。`)
    }
  }

  removeSavedBanners(results) {
    const successedBannerNames = results.map(banner => banner.name)
    this.banners = this.banners.filter(
      banner => !successedBannerNames.includes(banner.name),
    )
  }
}

/**
 * 1つのバナー用のフォーム
 */
export class AddBannerFormModel {
  constructor(data) {
    const {
      file,
      name,
      landingPageUrl,
      image,
      isCheckOverCompressionRate,
    } = data

    this.file = file
    this.name = name
    this.filename = name
    this.landingPageUrl = landingPageUrl
    this.isCheckOverCompressionRate = isCheckOverCompressionRate
    this.setImage(image)
  }

  get boundingSize() {
    return new BannerBoundingSizeModel({
      width: this.width,
      height: this.height,
    })
  }

  get regularBoundingSize() {
    const halfWidth = Math.floor(this.width / 2)
    const halfHeight = Math.floor(this.height / 2)
    return (
      BannerBoundingSizeModel.find(this.boundingSize) ||
      BannerBoundingSizeModel.find({ width: halfWidth, height: halfHeight })
    )
  }

  get normalizedBoundingSize() {
    return this.regularBoundingSize || this.boundingSize
  }

  get normalizedLandingPageUrl() {
    return this.landingPageUrl.trim().replace(' ', '%20')
  }

  /**
   * 規定のサイズリストと一致しなければイレギュラーとみなす。
   * ただし、倍サイズ画像は許容する。
   */
  get hasIrregularBoundingSize() {
    return this.regularBoundingSize == null
  }

  get isOverSize() {
    return isOverSize(this.file)
  }

  get isValid() {
    return validateUrl(this.landingPageUrl) && !this.hasIrregularBoundingSize
  }

  setImage(image) {
    this.image = image
    this.width = image.naturalWidth
    this.height = image.naturalHeight
  }

  async changeImage(file) {
    const { optimizedFile, isCheckOverCompressionRate } = await optimizeFile(
      file,
    )
    this.isCheckOverCompressionRate = isCheckOverCompressionRate
    if (optimizedFile instanceof Error) throw optimizedFile
    if (isOverSize(optimizedFile)) throw new OverSizeImageError()
    this.file = optimizedFile
    const image = await loadImage(optimizedFile)
    this.setImage(image)
  }
}
